<template>
  <div class="about-page">

    <!-- ═══════════════════ HERO SECTION (fondo neutro) ═══════════════════ -->
    <section class="about-hero">
      <div class="container hero-content">
        <div class="row align-items-center g-5">
          <div class="col-lg-6">
            <div class="hero-badge mb-3">
              <i class="fa-solid fa-circle-info me-2"></i>
              {{ $t('about.title') }}
            </div>
            <h1 class="hero-title">{{ $t('about.title') }}</h1>
            <p class="hero-description" v-html="$t('about.description')"></p>
          </div>
          <div class="col-lg-6 d-none d-lg-block">
            <div class="hero-image-wrapper">
              <img src="../assets/images/about_header.png" alt="About Aprèn" class="hero-image" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════ OBJECTIVES — full width gradient ═══════════════════ -->
    <section class="objectives-fullwidth">
      <div class="container">
        <div class="section-header">
          <div class="section-icon-wrap icon-light">
            <i class="fa-solid fa-bullseye"></i>
          </div>
          <div>
            <h2 class="section-title section-title-light">{{ $t('about.objectives.title') }}</h2>
          </div>
        </div>

        <div class="objectives-grid">
          <div
            v-for="(objective, index) in $tm('about.objectives.list')"
            :key="index"
            class="objective-item"
            :style="{ animationDelay: `${index * 0.08}s` }"
          >
            <div class="objective-number">{{ String(index + 1).padStart(2, '0') }}</div>
            <div class="objective-content" v-html="$rt(objective)"></div>
          </div>
        </div>
      </div>
    </section>

    <div class="container main-content">

      <!-- ═══════════════════ WHAT CAN I FIND — 2 columnas ═══════════════════ -->
      <section class="content-section">
        <div class="section-header">
          <div class="section-icon-wrap icon-teal">
            <i class="fa-solid fa-magnifying-glass"></i>
          </div>
          <div>
            <h2 class="section-title">{{ $t('about.what_find.title') }}</h2>
            <p class="section-subtitle">{{ $t('about.what_find.intro') }}</p>
          </div>
        </div>

        <div class="features-grid">
          <div
            class="feature-card"
            v-for="(item, key) in $tm('about.what_find.items')"
            :key="key"
          >
            <div class="feature-icon-box" :class="getIconBgClass(key)">
              <i :class="getIconForKey(key)"></i>
            </div>
            <div class="feature-body">
              <h3 class="feature-title">{{ $rt(item.title) }}</h3>
              <p class="feature-desc">{{ $rt(item.desc) }}</p>
            </div>
            <div class="feature-arrow">
              <i class="fa-solid fa-arrow-right"></i>
            </div>
          </div>
        </div>
      </section>

      <!-- ═══════════════════ CONTENT TYPES — 3 columnas ═══════════════════ -->
      <section class="content-section pb-5">
        <div class="section-header">
          <div class="section-icon-wrap icon-indigo">
            <i class="fa-solid fa-cubes"></i>
          </div>
          <div>
            <h2 class="section-title">{{ $t('about.types.title') }}</h2>
          </div>
        </div>

        <div class="types-grid">
          <div
            class="type-card"
            v-for="(group, key) in $tm('about.types.groups')"
            :key="key"
          >
            <div class="type-card-header" :class="getTypeHeaderClass(key)">
              <i :class="getGroupIcon(key)" class="type-card-icon"></i>
              <h3 class="type-card-title">{{ $rt(group.title) }}</h3>
            </div>
            <ul class="type-list">
              <li v-for="(item, idx) in group.items" :key="idx" class="type-list-item">
                <span class="type-bullet"></span>
                {{ $rt(item) }}
              </li>
            </ul>
          </div>
        </div>
      </section>

    </div>
  </div>
</template>

<script>
import { defineComponent, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import store from "@/store";

export default defineComponent({
  name: 'AboutView',
  setup() {
    const route = useRoute();
    const { tm, rt } = useI18n();

    onMounted(async () => {
      await store.dispatch('updateSearchSelected', "Tots");
      await store.dispatch('updateBackgroundImage', {
        view: route.name,
        url: require('@/assets/images/sections/banner-blue.webp')
      });
    });

    const getIconForKey = (key) => {
      const icons = {
        professorat: 'fa-solid fa-users',
        organitzacio: 'fa-solid fa-building',
        assignatures: 'fa-solid fa-sitemap',
        titulacions: 'fa-solid fa-user-graduate',
        activitats: 'fa-solid fa-university',
        innovacio: 'fa-solid fa-wand-magic-sparkles',
        publicacions: 'fa-regular fa-file-lines',
        acces_obert: 'fa-solid fa-lock-open',
        tfe: 'fa-solid fa-graduation-cap',
        videos: 'fa-solid fa-video'
      };
      return icons[key] || 'fa-solid fa-circle';
    };

    const getIconBgClass = (key) => {
      const colors = [
        'icon-blue-1', 'icon-blue-2', 'icon-blue-3', 'icon-blue-4',
        'icon-blue-5', 'icon-blue-1', 'icon-blue-2', 'icon-blue-3',
        'icon-blue-4', 'icon-blue-5'
      ];
      const index = Object.keys({
        professorat: 1, organitzacio: 1, assignatures: 1, titulacions: 1,
        activitats: 1, innovacio: 1, publicacions: 1, acces_obert: 1,
        tfe: 1, videos: 1
      }).indexOf(key);
      return colors[index] || 'icon-blue-1';
    };

    const getGroupIcon = (key) => {
      const icons = {
        publicacions: 'fa-solid fa-book-open',
        activitats: 'fa-solid fa-chalkboard-user',
        innovacio: 'fa-solid fa-lightbulb',
        formacio: 'fa-solid fa-certificate',
        premis: 'fa-solid fa-trophy',
        tutories: 'fa-solid fa-handshake-angle'
      };
      return icons[key] || 'fa-solid fa-folder';
    };

    const getTypeHeaderClass = (key) => {
      const classes = {
        publicacions: 'type-header-blue',
        activitats: 'type-header-cyan',
        innovacio: 'type-header-indigo',
        formacio: 'type-header-sky',
        premis: 'type-header-royal',
        tutories: 'type-header-steel'
      };
      return classes[key] || 'type-header-blue';
    };

    return {
      tm,
      rt,
      getIconForKey,
      getIconBgClass,
      getGroupIcon,
      getTypeHeaderClass
    };
  }
});
</script>

<style scoped>
/* ════════════════════════════════════════════════
   DESIGN TOKENS — PALETA APREN AZUL
════════════════════════════════════════════════ */
.about-page {
  --apren-900: #0c2340;
  --apren-800: #0d3160;
  --apren-700: #0a4a8a;
  --apren-600: #007BC1;
  --apren-500: #0a66c2;
  --apren-400: #1e90d4;
  --apren-300: #38b6e8;
  --apren-200: #a8d8f0;
  --apren-100: #dff0fb;
  --apren-50:  #f0f8ff;

  --text-primary: #0c2340;
  --text-secondary: #4a6278;
  --text-muted: #7a93a8;

  --surface: #ffffff;
  --surface-soft: #f5f9fd;
  --border: #d6e8f5;

  /* Tipografía global Aprèn */
  font-family: 'Roboto', Helvetica, serif;
  font-weight: 300;
  font-size: 18px;
  line-height: 28px;
}

/* ════════════════════════════════════════════════
   PAGE WRAPPER — sin fondo propio, hereda el de Aprèn
════════════════════════════════════════════════ */
.about-page {
  background: transparent;
  min-height: 100vh;
}

/* ════════════════════════════════════════════════
   HERO SECTION — sin fondo, hereda el de la página
════════════════════════════════════════════════ */
.about-hero {
  background: transparent;
  padding: 2rem 0 1.5rem;
}

.hero-content {
  position: relative;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  background: var(--apren-100);
  border: 1px solid var(--apren-200);
  color: var(--apren-700);
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.35rem 1rem;
  border-radius: 50px;
}

.hero-title {
  font-size: clamp(1.8rem, 3.5vw, 2.6rem);
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.15;
  margin: 0 0 1.25rem;
  border: none;
  padding: 0;
  background: none;
}

.hero-description {
  font-size: 18px;
  font-weight: 300;
  line-height: 28px;
  color: var(--text-secondary);
  margin: 0;
}

.hero-image-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-image {
  width: 100%;
  max-width: 500px;
  border-radius: 20px;
  box-shadow: 0 16px 48px rgba(10, 102, 194, 0.18);
  object-fit: cover;
}

/* ════════════════════════════════════════════════
   OBJECTIVES — full-width blue gradient
   Escapa el container-fluid del App.vue con márgenes negativos
════════════════════════════════════════════════ */
.objectives-fullwidth {
  background: linear-gradient(135deg, #0c2340 0%, #0d3d6b 45%, #0a66c2 100%);
  padding: 3rem 0 3.5rem;
  /* Escapar el padding del container-fluid padre */
  margin-left: calc(-50vw + 50%);
  margin-right: calc(-50vw + 50%);
  /* Compensar con padding interno para que el .container quede centrado */
  padding-left: calc(50vw - 50%);
  padding-right: calc(50vw - 50%);
  margin-bottom: 0;
  box-sizing: border-box;
}

.objectives-grid {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  margin-top: 0.5rem;
}

.objective-item {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  background: rgba(255, 255, 255, 0.10);
  backdrop-filter: blur(6px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-left: 4px solid rgba(255, 255, 255, 0.55);
  border-radius: 12px;
  padding: 1.1rem 1.5rem;
  transition: all 0.25s ease;
  animation: slideIn 0.4s ease both;
}

@keyframes slideIn {
  from { opacity: 0; transform: translateX(-16px); }
  to   { opacity: 1; transform: translateX(0); }
}

.objective-item:hover {
  transform: translateX(6px);
  background: rgba(255, 255, 255, 0.16);
  border-left-color: rgba(255,255,255,0.85);
}

.objective-number {
  font-size: 2rem;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.25);
  line-height: 1;
  min-width: 46px;
  letter-spacing: -0.04em;
  text-align: right;
  flex-shrink: 0;
  padding-top: 2px;
}

.objective-content {
  font-size: 18px;
  font-weight: 300;
  line-height: 28px;
  color: rgba(255, 255, 255, 0.92);
  padding-top: 2px;
}

/* ════════════════════════════════════════════════
   MAIN CONTENT (container)
════════════════════════════════════════════════ */
.main-content {
  padding-top: 2.5rem;
}

/* ════════════════════════════════════════════════
   SECTION COMMON
════════════════════════════════════════════════ */
.content-section {
  margin-bottom: 3rem;
}

.section-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 2rem;
}

.section-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, #007BC1, #0a66c2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: white;
  flex-shrink: 0;
  box-shadow: 0 6px 18px rgba(10, 102, 194, 0.30);
}

.section-icon-wrap.icon-light {
  background: rgba(255, 255, 255, 0.20);
  backdrop-filter: blur(6px);
  border: 1px solid rgba(255, 255, 255, 0.35);
  box-shadow: none;
}

.section-icon-wrap.icon-teal {
  background: linear-gradient(135deg, #0891b2, #0ea5e9);
  box-shadow: 0 6px 18px rgba(8, 145, 178, 0.30);
}

.section-icon-wrap.icon-indigo {
  background: linear-gradient(135deg, #0c2340, #0a4a8a);
  box-shadow: 0 6px 18px rgba(12, 35, 64, 0.30);
}

.section-title {
  font-size: 1.45rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.2rem;
  line-height: 1.3;
}

.section-title-light {
  color: #ffffff;
}

.section-subtitle {
  font-size: 18px;
  font-weight: 300;
  line-height: 28px;
  color: var(--text-secondary);
  margin: 0;
}

/* ════════════════════════════════════════════════
   FEATURES GRID — 2 columnas fijas
════════════════════════════════════════════════ */
.features-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.1rem;
}

.feature-card {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  background: white;
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 1.25rem 1.4rem;
  cursor: default;
  transition: all 0.28s cubic-bezier(0.165, 0.84, 0.44, 1);
  position: relative;
  overflow: hidden;
}

.feature-card::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  background: linear-gradient(90deg, #007BC1, #38b6e8);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 14px 32px rgba(10, 102, 194, 0.14);
  border-color: var(--apren-300);
}

.feature-card:hover::before {
  transform: scaleX(1);
}

.feature-card:hover .feature-arrow {
  opacity: 1;
  transform: translateX(0);
}

.feature-icon-box {
  width: 58px;
  height: 58px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: white;
  flex-shrink: 0;
}

.icon-blue-1 { background: linear-gradient(135deg, #007BC1, #0a66c2); box-shadow: 0 4px 12px rgba(0,123,193,0.35); }
.icon-blue-2 { background: linear-gradient(135deg, #0891b2, #0a66c2); box-shadow: 0 4px 12px rgba(8,145,178,0.35); }
.icon-blue-3 { background: linear-gradient(135deg, #0c2340, #0a4a8a); box-shadow: 0 4px 12px rgba(12,35,64,0.30); }
.icon-blue-4 { background: linear-gradient(135deg, #1e90d4, #38b6e8); box-shadow: 0 4px 12px rgba(30,144,212,0.35); }
.icon-blue-5 { background: linear-gradient(135deg, #0d3d6b, #007BC1); box-shadow: 0 4px 12px rgba(13,61,107,0.35); }

.feature-body {
  flex: 1;
  min-width: 0;
}

.feature-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.35rem;
  line-height: 1.4;
}

.feature-desc {
  font-size: 18px;
  font-weight: 300;
  line-height: 28px;
  color: var(--text-muted);
  margin: 0;
}

.feature-arrow {
  color: var(--apren-400);
  font-size: 0.9rem;
  opacity: 0;
  transform: translateX(-8px);
  transition: all 0.25s ease;
  flex-shrink: 0;
}

/* ════════════════════════════════════════════════
   CONTENT TYPES GRID — 3 columnas
════════════════════════════════════════════════ */
.types-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;
}

.type-card {
  background: white;
  border: 1px solid var(--border);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.28s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.type-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 16px 36px rgba(10, 102, 194, 0.13);
}

.type-card-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
}

.type-header-blue   { background: linear-gradient(135deg, #007BC1, #0a66c2); }
.type-header-cyan   { background: linear-gradient(135deg, #0891b2, #06b6d4); }
.type-header-indigo { background: linear-gradient(135deg, #0c2340, #0a4a8a); }
.type-header-sky    { background: linear-gradient(135deg, #0ea5e9, #38b6e8); }
.type-header-royal  { background: linear-gradient(135deg, #1e3a6e, #007BC1); }
.type-header-steel  { background: linear-gradient(135deg, #334155, #0d3d6b); }

.type-card-icon {
  font-size: 1.2rem;
  color: rgba(255,255,255,0.9);
}

.type-card-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: white;
  margin: 0;
  line-height: 1.3;
}

.type-list {
  padding: 0.85rem 1.25rem 1rem;
  margin: 0;
  list-style: none;
}

.type-list-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 16px;
  font-weight: 300;
  line-height: 26px;
  color: var(--text-secondary);
  padding: 0.3rem 0;
  border-bottom: 1px solid #f0f6fb;
}

.type-list-item:last-child {
  border-bottom: none;
}

.type-bullet {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--apren-400);
  flex-shrink: 0;
}

/* ════════════════════════════════════════════════
   RESPONSIVE
════════════════════════════════════════════════ */
@media (max-width: 992px) {
  .types-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .about-hero {
    padding: 2rem 0 1.5rem;
  }

  .hero-title {
    font-size: 1.7rem;
  }

  .features-grid {
    grid-template-columns: 1fr;
  }

  .types-grid {
    grid-template-columns: 1fr;
  }

  .objectives-fullwidth {
    padding: 2rem 0 2.5rem;
  }

  .objective-number {
    font-size: 1.5rem;
    min-width: 36px;
  }
}

@media (max-width: 480px) {
  .features-grid {
    grid-template-columns: 1fr;
  }

  .feature-arrow {
    display: none;
  }
}
</style>
